import type {
  ShipmentDeliveryIssue,
  ShipmentHandoverState,
  ShipmentStatus,
} from '@/api';

import { addMonths, today } from './dates';
import { formatDate, toNumber } from './format';
import { overdueDays } from './paymentSchedule';
import { DELIVERY_ISSUES } from './shipmentStatus';

/*
 * «Текущая задача» отгрузки - что сделать, чтобы она пошла дальше. Одно правило на
 * баннер карточки и карточку списка «Отгрузки в работе», иначе тексты разойдутся.
 * Текст говорит о переходе, который система делает на самом деле: «Подготовка»
 * наступает, когда 1С создаёт заказ, «Оплата» - когда 1С его отгружает,
 * «Завершено» - когда 1С видит оплату (ADR-013). У закрытой отгрузки задачи нет.
 */

/** Что нужно, чтобы назвать задачу: есть у строки списка, карточка собирает из своих полей. */
export interface NextTaskSource {
  status: ShipmentStatus;
  plannedDate: string;
  handoverState: ShipmentHandoverState;
  clientAgreedAt?: string | null;
  stockConfirmedAt?: string | null;
  clientReconfirmedAt?: string | null;
  deliveryIssues: ReadonlyArray<ShipmentDeliveryIssue>;
  /** Пунктов в чек-листе документов. */
  documentCount: number | string;
  /** Отмеченных пунктов чек-листа. */
  markedDocumentCount: number | string;
  trackingNumber?: string | null;
  dispatchDate?: string | null;
  /** Последняя дата графика оплаты заказа 1С. */
  paymentDueDate?: string | null;
}

export interface NextTaskText {
  title: string;
  hint: string;
}

export const resolveNextTaskText = (
  source: NextTaskSource,
): NextTaskText | null => {
  const task = (title: string, hint: string): NextTaskText => ({
    title,
    hint,
  });

  switch (source.status) {
    case 'Planned1':
      return task(
        isDueForWork(source.plannedDate)
          ? 'Взять в работу: до отгрузки месяц или меньше'
          : 'Взять в работу, когда подойдёт срок согласования',
        'После «Взять в работу» отгрузка перейдёт в «Согласование»',
      );
    case 'Planned2':
      return resolveApprovalTask(source);
    case 'Agreed':
      return task(
        resolvePreparingTitle(source),
        'После «Передать в доставку» - «Доставка»; когда 1С отгрузит заказ - «Оплата» или «Завершено»',
      );
    case 'Delivery':
      return task(
        source.trackingNumber || source.dispatchDate
          ? 'Доставить груз клиенту'
          : 'Внести трек-номер, если груз едет с перевозчиком',
        'Когда 1С отгрузит заказ целиком, отгрузка перейдёт в «Оплату» или «Завершено»',
      );
    case 'Payment':
      return task(
        resolvePaymentTitle(source.paymentDueDate),
        'Когда 1С увидит оплату, отгрузка перейдёт в «Завершено»',
      );
    default:
      return null;
  }
};

/**
 * Запланированную берут в работу за месяц до плановой даты; автоматического
 * перехода пока нет, и такую отгрузку «Мои задачи» показывают задачей (сервер:
 * ShipmentTasks).
 */
const isDueForWork = (plannedDate: string): boolean =>
  plannedDate <= addMonths(today(), 1);

/** На «Оплате» - срок по графику заказа; прошёл - сколько дней просрочки. */
const resolvePaymentTitle = (date: string | null | undefined): string => {
  if (!date) return 'Ждём оплату клиента';

  const overdue = overdueDays(date, today());

  return overdue === null
    ? `Ждём оплату до ${formatDate(date)}`
    : `Оплата просрочена на ${overdue} дн.: срок был ${formatDate(date)}`;
};

/** На «Согласовании» задача зависит от передачи в 1С: она идёт поверх статуса. */
const resolveApprovalTask = (source: NextTaskSource): NextTaskText => {
  const toPreparing =
    'Когда 1С создаст заказ, отгрузка перейдёт в «Подготовку»';

  switch (source.handoverState) {
    case 'InFlight':
      return {
        title: 'Ждём 1С: заказ клиента создаётся',
        hint: 'Ответ обычно приходит за минуту-две, затем отгрузка перейдёт в «Подготовку»',
      };
    case 'NoResponse':
      return {
        title: 'Нет ответа от 1С: запросите исход',
        hint: 'Заказ мог создаться - повторный запрос второго заказа не создаст',
      };
    case 'Rejected':
      return {
        title: '1С отказала: исправьте отгрузку и отправьте снова',
        hint: toPreparing,
      };
    default:
      return { title: resolveApprovalStepTitle(source), hint: toPreparing };
  }
};

/**
 * На «Подготовке» - чего ждёт заказ в 1С, если ждёт; иначе документы, пока
 * чек-лист не отмечен (передать в доставку они не мешают), и сама передача.
 */
const resolvePreparingTitle = (source: NextTaskSource): string => {
  const waiting = source.deliveryIssues.find(
    (issue) => issue !== 'NotPreparing',
  );
  if (waiting) return DELIVERY_ISSUES[waiting];

  const marked = toNumber(source.markedDocumentCount);
  const total = toNumber(source.documentCount);

  return marked < total
    ? `Подготовить документы и передать в доставку: отмечено ${marked} из ${total}`
    : 'Передать отгрузку в доставку';
};

/** Что делать на «Согласовании» - первый неотмеченный шаг, а после всех - заказ в 1С. */
const resolveApprovalStepTitle = (source: NextTaskSource): string => {
  if (!source.clientAgreedAt)
    return 'Связаться с клиентом и согласовать дату, объём и адрес';
  if (!source.stockConfirmedAt) return 'Проверить остаток на складе';
  if (!source.clientReconfirmedAt)
    return 'Повторно подтвердить с клиентом дату, объём и позиции';
  return 'Сформировать заказ в 1С';
};
